import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ChevronDown, ChevronUp, X } from '@mynaui/icons-react';
import { ArrowDownToDot, Download, Droplets, RotateCcw } from 'lucide-react';

import {
  rainfallGullyTaggingAtom,
  rainfallSelectedFeatureAtom,
  rainfallSelectedPondIdAtom,
  rainfallStormScaleAtom,
} from '@/atoms/rainfall.atom';
import { selectedFeatureAtom } from '@/atoms/selection.atom';
import { Slider } from '@/components/ui/slider';
import { getLinkSessionId } from '@/graphql/execute';
import { loadFeaturesByLayerOfflineAware } from '@/lib/offline/offlineFeatures';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { downloadFile } from '@/lib/download';
import { buildRainfallCsv, safeRainfallCsvFilename } from '@/lib/rainfallCsvExport';
import { isPondDrained } from '@/lib/rainfallStyling';
import {
  RAINFALL_SCALE_DEFAULT,
  RAINFALL_SCALE_MAX,
  RAINFALL_SCALE_MIN,
  RAINFALL_SCALE_SNAP_EPSILON,
  formatScale,
  scaledPondMetrics,
  scaledStormDepth,
  scaledStormIntensity,
  scaledTotalRunoffM3,
} from '@/lib/rainfallScaling';

import { RainfallControlsPanel } from './RainfallControlsPanel';
import { fmtDurationS } from './RainfallFeatureView';

/**
 * Renders the per-run rainfall metrics (`metadata.rainfallSummary`) on
 * the GROUP layer that postprocess produces from a RAINFALL_ANALYSIS
 * job. Mirrors `DeviationVolumePanel`'s collapsed/expanded/dismissed
 * state model so the two analysis panels feel consistent next to each
 * other in the viewer overlay.
 *
 * Display states:
 *   - **collapsed** (default): one-liner with pond count + total ponded
 *     volume, so the user always sees the headline result.
 *   - **expanded**: full KPI grid + pond/flow-line breakdown.
 *   - **dismissed**: panel hidden until the user re-shows it (per-layer
 *     setting persisted in sessionStorage).
 */

interface RainfallSummary {
  mode?: 'TOPOLOGY' | 'STORM' | string;
  pondCount?: number;
  terminalPondCount?: number;
  totalPondedVolumeM3?: number;
  totalFlowLineLengthM?: number;
  maxFlowAccumulationM2?: number;
  meanTriangleAreaM2?: number;
  degenerateTrianglesDropped?: number;
  // Log-scale flow-accumulation ramp anchors (from worker.py step 7).
  min?: number;
  max?: number;
  p2?: number;
  p50?: number;
  p98?: number;
  // Storm-mode aggregates (only present when mode === 'STORM').
  storm?: {
    depthMm?: number;
    durationH?: number;
    runoffCoefficient?: number;
    intensityMmPerHr?: number;
  };
  totalRunoffM3?: number;
}

interface RainfallParams {
  sinkHandling?: 'FILL' | 'BREACH' | string;
  resolutionM?: number;
  minPondDepthM?: number;
  minPondAreaM2?: number;
  mode?: 'TOPOLOGY' | 'STORM' | string;
  stormDepthMm?: number;
  stormDurationH?: number;
  runoffCoefficient?: number;
}

type PanelState = 'collapsed' | 'expanded' | 'dismissed';

const panelStateStorage = createJSONStorage<PanelState>(() => sessionStorage);

const summaryPanelStateAtom = atomFamily((layerId: string) =>
  atomWithStorage<PanelState>(
    `@actegon.gis.rainfall.summary.${layerId}`,
    'collapsed',
    panelStateStorage,
  ),
);

// Leaderboard expanded/collapsed — per layer + persisted alongside the
// summary panel state. The default is computed at render time from the
// pondCount (≤ 8 → expanded), but once the user toggles it we honour
// their choice.
const leaderboardExpandedAtom = atomFamily((layerId: string) =>
  atomWithStorage<boolean | null>(
    `@actegon.gis.rainfall.leaderboard.${layerId}`,
    null,
    createJSONStorage<boolean | null>(() => sessionStorage),
  ),
);

interface Props {
  layerId: string;
  layerName: string;
  summary: RainfallSummary;
  params?: RainfallParams;
}

const fmtCount = (n: number | undefined): string =>
  n != null && Number.isFinite(n) ? n.toLocaleString() : '—';

const fmtVolume = (m3: number | undefined): string => {
  if (m3 == null || !Number.isFinite(m3)) return '—';
  // Show 4 decimals when below 1 m³ (otherwise small ponds round to "0.00").
  return m3 >= 1 ? `${m3.toFixed(2)} m³` : `${m3.toFixed(4)} m³`;
};

const fmtArea = (m2: number | undefined): string => {
  if (m2 == null || !Number.isFinite(m2)) return '—';
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(2)} ha`;
  return `${m2.toFixed(2)} m²`;
};

const fmtLengthM = (m: number | undefined): string => {
  if (m == null || !Number.isFinite(m)) return '—';
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${m.toFixed(1)} m`;
};

const fmtLog10 = (v: number | undefined): string => {
  if (v == null || !Number.isFinite(v)) return '—';
  // The summary's min/max/p* are log10(facc m²). Convert back to a
  // human-readable upstream-area number so the KPI is interpretable
  // without remembering "this is log10".
  const linear = Math.pow(10, v);
  if (linear >= 10_000) return `${(linear / 10_000).toFixed(1)} ha`;
  if (linear >= 1000) return `${linear.toFixed(0)} m²`;
  return `${linear.toFixed(1)} m²`;
};

const fmtRes = (m: number | undefined): string =>
  m != null && Number.isFinite(m) ? `${m.toFixed(2)} m` : '—';

export function RainfallSummaryPanel({ layerId, layerName, summary, params }: Props) {
  const { t } = useTranslation('project');
  const { projectId } = useParams();
  const [state, setState] = useAtom(summaryPanelStateAtom(layerId));
  const gullies = useAtomValue(rainfallGullyTaggingAtom);
  const [stormScale, setStormScale] = useAtom(rainfallStormScaleAtom);
  const isScaled = Math.abs(stormScale - RAINFALL_SCALE_DEFAULT) > 1e-6;
  // Scaled aggregates for the storm footer + headline KPI. The
  // un-scaled raw values stay accessible (intensityMmPerHr,
  // totalRunoffM3) so the footer can show "design × multiplier =
  // scaled" inline.
  const scaledRunoffTotalM3 = scaledTotalRunoffM3(summary.totalRunoffM3, stormScale);
  const scaledIntensity = scaledStormIntensity(summary.storm?.intensityMmPerHr, stormScale);
  const scaledDepth = scaledStormDepth(summary.storm?.depthMm, stormScale);

  // Drained-pond count — counts pondIds belonging to *this* layer that
  // the user has tagged as drained. We pull pond IDs from the same
  // features cache the leaderboard reads, which Viewer.tsx populates at
  // mount time. Read-only `staleTime: Infinity` so we never trigger an
  // extra fetch from this hook.
  const featuresQuery = useQuery({
    queryKey: ['features', layerId],
    queryFn: () => loadFeaturesByLayerOfflineAware([layerId], projectId!),
    staleTime: Infinity,
  });
  const drainedCount = useMemo(() => {
    const features = featuresQuery.data ?? [];
    let n = 0;
    for (const f of features) {
      const p = (f.properties ?? {}) as Record<string, unknown>;
      if (p.kind !== 'pond') continue;
      if (isPondDrained(p, gullies)) n += 1;
    }
    return n;
  }, [featuresQuery.data, gullies]);

  if (!summary) return null;

  const isExpanded = state === 'expanded';
  const sink = params?.sinkHandling ?? 'BREACH';

  if (state === 'dismissed') {
    return (
      <button
        type='button'
        onClick={() => setState('collapsed')}
        className='pointer-events-auto self-end rounded-md border border-slate-200/70 bg-white/80 px-2 py-0.5 text-[11px] text-slate-600 shadow-sm backdrop-blur-md hover:bg-white hover:text-slate-900 dark:border-stone-700/70 dark:bg-stone-900/80 dark:text-stone-300 dark:hover:bg-stone-900 dark:hover:text-white'
        title={t('rainfallSummary.show') as string}>
        {t('rainfallSummary.show')}
      </button>
    );
  }

  return (
    // The whole panel is constrained to the viewport with a small
    // safety margin (top-4 + bottom-4 elsewhere → 2rem total). Keep
    // the header outside the scroll container so collapse/dismiss
    // stay clickable when the inspector pushes the body past the
    // viewport bottom — common for ponds with full storm-mode stats.
    <div className='pointer-events-auto flex max-h-full w-full flex-col rounded-md border border-slate-200/70 bg-white/90 px-3 pt-1.5 pb-2 text-xs shadow-sm backdrop-blur-md dark:border-stone-700/70 dark:bg-stone-900/90'>
      <div className='flex shrink-0 items-center justify-between gap-2'>
        <button
          type='button'
          onClick={() => setState(isExpanded ? 'collapsed' : 'expanded')}
          className='flex flex-1 items-center gap-1.5 text-left'
          title={(isExpanded ? t('rainfallSummary.collapse') : t('rainfallSummary.expand')) as string}>
          {isExpanded ? <ChevronUp className='size-3.5' /> : <ChevronDown className='size-3.5' />}
          <Droplets className='size-3.5 text-sky-600 dark:text-sky-400' />
          <span className='font-medium text-slate-900 dark:text-white'>
            {t('rainfallSummary.title')}
          </span>
          {!isExpanded && (
            <>
              <span className='ml-2 font-mono tabular-nums text-slate-700 dark:text-stone-200'>
                {t('rainfallSummary.pondCountInline', { count: summary.pondCount ?? 0 })}
              </span>
              {summary.totalPondedVolumeM3 != null && (
                <span className='ml-2 text-slate-500 dark:text-stone-400'>
                  · {fmtVolume(summary.totalPondedVolumeM3)}
                </span>
              )}
            </>
          )}
        </button>
        <div className='flex items-center gap-1'>
          <span
            className='max-w-[140px] truncate text-slate-500 dark:text-stone-400'
            title={layerName}>
            {layerName}
          </span>
          <button
            type='button'
            onClick={() => setState('dismissed')}
            title={t('rainfallSummary.dismiss') as string}
            className='size-5 rounded text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 dark:text-stone-400 dark:hover:bg-stone-700/70 dark:hover:text-white'>
            <X className='size-3.5' />
          </button>
        </div>
      </div>

      {isExpanded && (
        // Scrollable body. The panel's max-height already caps the
        // outer container at viewport-minus-margin; this child carries
        // the actual overflow so the header above stays anchored as
        // the user scrolls through KPIs + storm footer + quality
        // footer + embedded controls panel + inspector.
        //
        // `min-h-0` is load-bearing — flex items default to
        // `min-height: auto` (= intrinsic content height), which
        // overrides `max-height` on the parent and makes the
        // overflow:auto a no-op. Setting min-h-0 lets the flex parent
        // actually clip its child.
        <div className='mt-2 min-h-0 flex-1 overflow-y-auto pr-1'>
          <dl className='grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5'>
            <dt className='text-slate-600 dark:text-stone-300'>
              {t('rainfallSummary.pondCount')}
            </dt>
            <dd className='text-right font-mono tabular-nums text-slate-900 dark:text-white'>
              {fmtCount(summary.pondCount)}
            </dd>

            <dt className='text-slate-600 dark:text-stone-300'>
              {t('rainfallSummary.totalPondedVolume')}
            </dt>
            <dd className='text-right font-mono tabular-nums text-sky-600 dark:text-sky-400'>
              {fmtVolume(summary.totalPondedVolumeM3)}
            </dd>

            <dt className='text-slate-600 dark:text-stone-300'>
              {t('rainfallSummary.totalFlowLineLength')}
            </dt>
            <dd className='text-right font-mono tabular-nums text-slate-900 dark:text-white'>
              {fmtLengthM(summary.totalFlowLineLengthM)}
            </dd>

            <dt className='text-slate-600 dark:text-stone-300'>
              {t('rainfallSummary.maxFlowAccumulation')}
            </dt>
            <dd className='text-right font-mono tabular-nums text-slate-900 dark:text-white'>
              {fmtArea(summary.maxFlowAccumulationM2)}
            </dd>

            {/* Terminal pond count — system endpoints. Always present
                because we compute overflow chains in both modes. */}
            {summary.terminalPondCount != null && summary.terminalPondCount > 0 && (
              <>
                <dt className='text-slate-600 dark:text-stone-300'>
                  {t('rainfallSummary.terminalPondCount')}
                </dt>
                <dd className='text-right font-mono tabular-nums text-slate-900 dark:text-white'>
                  {fmtCount(summary.terminalPondCount)}
                </dd>
              </>
            )}

            {/* Drained pond count — derived from the user's gully
                tagging. Only renders when the user has tagged at
                least one pond, so the KPI doesn't waste a row in the
                common "no gullies tagged yet" case. */}
            {drainedCount > 0 && (
              <>
                <dt className='flex items-center gap-1 text-slate-600 dark:text-stone-300'>
                  <ArrowDownToDot className='size-3' />
                  {t('rainfallSummary.drainedPondCount')}
                </dt>
                <dd className='text-right font-mono tabular-nums text-slate-700 dark:text-stone-200'>
                  {fmtCount(drainedCount)}
                </dd>
              </>
            )}

            {/* Storm-only aggregate. Total rainfall volume that
                becomes runoff over the survey for the chosen storm.
                Scaled by the what-if multiplier; when scale ≠ 1× the
                value is italicised + tilde-prefixed to mark it as a
                scenario value. */}
            {summary.mode === 'STORM' && scaledRunoffTotalM3 != null && (
              <>
                <dt className='text-slate-600 dark:text-stone-300'>
                  {t('rainfallSummary.totalRunoff')}
                </dt>
                <dd
                  className={`text-right font-mono tabular-nums ${
                    isScaled
                      ? 'italic text-sky-700 dark:text-sky-300'
                      : 'text-sky-700 dark:text-sky-300'
                  }`}>
                  {isScaled ? `~${fmtVolume(scaledRunoffTotalM3)}` : fmtVolume(scaledRunoffTotalM3)}
                </dd>
              </>
            )}
          </dl>

          {/* Storm parameters footer — only renders for STORM-mode runs.
              Surfaces the design storm + intensity so the user can
              re-derive the runoff numbers, and matches what they entered
              in the dialog one-to-one. */}
          {summary.mode === 'STORM' && summary.storm && (
            <div className='mt-2 rounded border border-sky-200/70 bg-sky-50/40 p-1.5 text-[11px] text-slate-700 dark:border-sky-900/40 dark:bg-sky-950/20 dark:text-stone-300'>
              <div className='mb-0.5 flex items-center justify-between gap-2'>
                <span className='font-medium text-sky-700 dark:text-sky-400'>
                  {t('rainfallSummary.stormHeader')}
                </span>
                {isScaled && (
                  <span
                    className='rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
                    title={t('rainfallSummary.whatIfHint') as string}>
                    {t('rainfallSummary.showingScale', { x: formatScale(stormScale) })}
                  </span>
                )}
              </div>
              <div className='flex items-center justify-between gap-3'>
                <span>{t('rainfallSummary.stormDepth')}</span>
                <span className='font-mono tabular-nums'>
                  {summary.storm.depthMm != null ? `${summary.storm.depthMm.toFixed(0)} mm` : '—'}
                  {isScaled && scaledDepth != null && (
                    <span className='ml-1 italic text-sky-700 dark:text-sky-300'>
                      × {formatScale(stormScale)} = {scaledDepth.toFixed(0)} mm
                    </span>
                  )}
                </span>
              </div>
              <div className='flex items-center justify-between gap-3'>
                <span>{t('rainfallSummary.stormDuration')}</span>
                <span className='font-mono tabular-nums'>
                  {summary.storm.durationH != null
                    ? `${summary.storm.durationH.toFixed(2)} h`
                    : '—'}
                </span>
              </div>
              <div className='flex items-center justify-between gap-3'>
                <span>{t('rainfallSummary.stormIntensity')}</span>
                <span className='font-mono tabular-nums'>
                  {summary.storm.intensityMmPerHr != null
                    ? `${summary.storm.intensityMmPerHr.toFixed(1)} mm/h`
                    : '—'}
                  {isScaled && scaledIntensity != null && (
                    <span
                      className='ml-1 italic text-sky-700 dark:text-sky-300'
                      title={t('rainfallSummary.whatIfHint') as string}>
                      {t('rainfallSummary.scaledIntensity', {
                        x: formatScale(stormScale),
                        value: scaledIntensity.toFixed(1),
                      })}
                    </span>
                  )}
                </span>
              </div>
              <div className='flex items-center justify-between gap-3'>
                <span>{t('rainfallSummary.runoffCoefficient')}</span>
                <span className='font-mono tabular-nums'>
                  {summary.storm.runoffCoefficient != null
                    ? summary.storm.runoffCoefficient.toFixed(2)
                    : '—'}
                </span>
              </div>

              {/* What-if intensity slider — drives the
                  `rainfallStormScaleAtom`. Snaps to 1× near the centre
                  so the user can return to "design storm" without
                  fiddling. Plain in-memory atom — refreshing the page
                  resets the scenario. */}
              <div className='mt-1.5 border-t border-sky-200/50 pt-1.5 dark:border-sky-900/40'>
                <div className='mb-0.5 flex items-center justify-between gap-2'>
                  <span className='font-medium text-sky-700 dark:text-sky-400'>
                    {t('rainfallSummary.whatIfHeader')}
                  </span>
                  {isScaled && (
                    <button
                      type='button'
                      onClick={() => setStormScale(RAINFALL_SCALE_DEFAULT)}
                      className='inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] text-slate-500 hover:bg-sky-100 hover:text-slate-900 dark:text-stone-400 dark:hover:bg-sky-900/30 dark:hover:text-stone-100'
                      title={t('rainfallSummary.whatIfReset') as string}>
                      <RotateCcw className='size-3' />
                      {t('rainfallSummary.whatIfReset')}
                    </button>
                  )}
                </div>
                <div className='mb-0.5 text-[10px] italic text-slate-500 dark:text-stone-400'>
                  {t('rainfallSummary.whatIfHint')}
                </div>
                <div className='flex items-center gap-2'>
                  <Slider
                    value={[stormScale]}
                    min={RAINFALL_SCALE_MIN}
                    max={RAINFALL_SCALE_MAX}
                    step={0.05}
                    onValueChange={([v]) => {
                      // Snap to 1× when the user drags within 3 % of design.
                      // The centre detent matters: drainage engineers want
                      // to compare "back-to-design" against any modified
                      // value without hunting for a precise 1.000.
                      const snapped =
                        Math.abs(v - RAINFALL_SCALE_DEFAULT) < RAINFALL_SCALE_SNAP_EPSILON
                          ? RAINFALL_SCALE_DEFAULT
                          : v;
                      setStormScale(snapped);
                    }}
                    className='flex-1'
                  />
                  <span
                    className={`w-[88px] text-right font-mono tabular-nums text-[11px] ${
                      isScaled
                        ? 'font-medium text-sky-700 dark:text-sky-300'
                        : 'text-slate-700 dark:text-stone-200'
                    }`}>
                    {t('rainfallSummary.whatIfMultiplier', { x: formatScale(stormScale) })}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Run parameters footer — useful for "what configuration produced
              this result?" especially when comparing two rainfall runs side
              by side. */}
          {params && (
            <div className='mt-2 border-t border-slate-200/70 pt-1.5 text-[11px] text-slate-500 dark:border-stone-700/70 dark:text-stone-400'>
              <div className='flex items-center justify-between gap-3'>
                <span>{t('rainfallSummary.routing')}</span>
                <span className='font-mono'>
                  {sink === 'FILL'
                    ? t('rainfallAnalysis.sinkFill')
                    : t('rainfallAnalysis.sinkBreach')}
                </span>
              </div>
              <div className='flex items-center justify-between gap-3'>
                <span>{t('rainfallSummary.resolution')}</span>
                <span className='font-mono tabular-nums'>{fmtRes(params.resolutionM)}</span>
              </div>
              <div className='flex items-center justify-between gap-3'>
                <span>{t('rainfallSummary.minPondDepth')}</span>
                <span className='font-mono tabular-nums'>
                  {params.minPondDepthM != null
                    ? `${(params.minPondDepthM * 100).toFixed(1)} cm`
                    : '—'}
                </span>
              </div>
              <div className='flex items-center justify-between gap-3'>
                <span>{t('rainfallSummary.minPondArea')}</span>
                <span className='font-mono tabular-nums'>{fmtArea(params.minPondAreaM2)}</span>
              </div>
            </div>
          )}

          {/* Quality footer — surfaces the diagnostic stats from the
              worker (mean triangle area, dropped degenerates, raster
              ramp range). Helps the user see if their TIN was poor
              quality (lots of dropped triangles → suspect input). */}
          {(summary.meanTriangleAreaM2 != null ||
            summary.degenerateTrianglesDropped != null ||
            summary.p98 != null) && (
            <div className='mt-2 border-t border-slate-200/70 pt-1.5 text-[11px] text-slate-500 dark:border-stone-700/70 dark:text-stone-400'>
              {summary.meanTriangleAreaM2 != null && (
                <div className='flex items-center justify-between gap-3'>
                  <span>{t('rainfallSummary.meanTriangleArea')}</span>
                  <span className='font-mono tabular-nums'>
                    {fmtArea(summary.meanTriangleAreaM2)}
                  </span>
                </div>
              )}
              {summary.degenerateTrianglesDropped != null &&
                summary.degenerateTrianglesDropped > 0 && (
                  <div className='flex items-center justify-between gap-3 text-amber-600 dark:text-amber-400'>
                    <span>{t('rainfallSummary.degenerateDropped')}</span>
                    <span className='font-mono tabular-nums'>
                      {fmtCount(summary.degenerateTrianglesDropped)}
                    </span>
                  </div>
                )}
              {summary.p98 != null && (
                <div className='flex items-center justify-between gap-3'>
                  <span>{t('rainfallSummary.flowAccumulationRange')}</span>
                  <span className='font-mono tabular-nums'>
                    {fmtLog10(summary.p2)} – {fmtLog10(summary.p98)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Time-to-overflow leaderboard — STORM-mode only. Reads pond
              features straight from the layer's GraphQL cache. */}
          {summary.mode === 'STORM' && (summary.pondCount ?? 0) > 0 && (
            <div className='mt-2 border-t border-slate-200/70 pt-1.5 dark:border-stone-700/70'>
              <TimeToOverflowLeaderboard
                layerId={layerId}
                layerName={layerName}
                totalRunoffM3={summary.totalRunoffM3}
                stormDurationH={summary.storm?.durationH}
              />
            </div>
          )}

          {/* Inline controls — visibility toggles, accumulation slider,
              feature inspector. Embedded inside the summary's expanded
              section so dismissing the summary closes the controls too,
              and so the user always sees one panel per rainfall result
              instead of two stacked. */}
          <div className='mt-3 border-t border-slate-200/70 pt-2 dark:border-stone-700/70'>
            <RainfallControlsPanel summary={summary} />
          </div>
        </div>
      )}
    </div>
  );
}

/** Loose accessor view of a feature row — wide enough for the
 *  leaderboard's structural reads (id, layerId, properties) but
 *  narrow enough to drop the GraphQL `__typename` / `geometry` fields
 *  the row click-through doesn't need. The CSV export and drawer
 *  push the original GQL feature through, the row only needs to
 *  destructure these three. */
type FeatureLike = {
  id?: string | null;
  layerId?: string | null;
  properties?: Record<string, unknown> | null;
};

interface PondRow {
  pondId: string;
  /** Scaled fill time (s). Already divided by `stormScale`. */
  fillTimeS: number;
  volumeM3: number;
  /** Pond's per-feature scaled total runoff (m³) for the captureRatio
   *  display. The leaderboard shows the same ratio as the inspector
   *  capacity chip so the two views agree at any scale. */
  scaledTotalRunoffM3: number | null;
  drained: boolean;
  /** The original GraphQL `Feature` row — kept as `unknown` so the
   *  click-through can hand it back to `setDrawerFeature` (which wants
   *  the full shape) without us re-stating every field. We only ever
   *  reach into its `properties` / `id` / `layerId` here. */
  feature: FeatureLike & Record<string, unknown>;
}

function TimeToOverflowLeaderboard({
  layerId,
  layerName,
  totalRunoffM3,
  stormDurationH,
}: {
  layerId: string;
  layerName: string;
  totalRunoffM3?: number;
  stormDurationH?: number;
}) {
  const { t } = useTranslation('project');
  const { projectId } = useParams();
  const [storedExpanded, setExpanded] = useAtom(leaderboardExpandedAtom(layerId));
  const gullies = useAtomValue(rainfallGullyTaggingAtom);
  const stormScale = useAtomValue(rainfallStormScaleAtom);
  const isScaled = Math.abs(stormScale - RAINFALL_SCALE_DEFAULT) > 1e-6;
  const stormDurationS = stormDurationH != null ? stormDurationH * 3600 : undefined;
  const setSelectedFeature = useSetAtom(rainfallSelectedFeatureAtom);
  const setSelectedPondId = useSetAtom(rainfallSelectedPondIdAtom);
  // Drawer-side selection: clicking a leaderboard row should also open
  // the FeatureProperties drawer with the pond's full inspector view,
  // mirroring what a click on the polygon does. Without this, the row
  // only updated the rainfall-internal "selected pond" atom — the user
  // saw the watershed isolate but had to additionally click the polygon
  // to read capacity / fill-time / chain context.
  const setDrawerFeature = useSetAtom(selectedFeatureAtom);
  const { createUpdateHandler } = useFeatureActions();

  // Read features from the cache populated by Viewer.tsx's useQueries.
  // We deliberately skip refetch (`enabled: false` would also cancel
  // the Viewer's mount-time query). React-Query dedupes the same
  // queryKey across consumers, so we just pull whatever's already
  // there.
  const featuresQuery = useQuery({
    queryKey: ['features', layerId],
    queryFn: () => loadFeaturesByLayerOfflineAware([layerId], projectId!),
    staleTime: Infinity,
  });

  // The leaderboard reads off the GraphQL `Feature` (with its full
  // shape — geometry, serial, etc.) but only needs `id`/`layerId`/
  // `properties`. We keep the original union type rather than
  // narrowing so the same array can be passed to `setDrawerFeature`
  // without losing the fields the drawer expects.
  const features = featuresQuery.data ?? [];

  const { ponds, drainedCount } = useMemo(() => {
    const result: PondRow[] = [];
    let drained = 0;
    for (const f of features) {
      const props = (f.properties ?? {}) as Record<string, unknown>;
      if (props.kind !== 'pond') continue;
      const pondId = props.pondId as string | undefined;
      if (!pondId) continue;
      const isDrained = isPondDrained(props, gullies);
      if (isDrained) {
        drained += 1;
        continue;
      }
      const scaled = scaledPondMetrics(props, stormScale);
      const fillTime = scaled.fillTimeS;
      if (fillTime == null) continue;
      result.push({
        pondId,
        fillTimeS: fillTime,
        volumeM3: scaled.volumeM3 ?? 0,
        scaledTotalRunoffM3: scaled.totalRunoffM3,
        drained: false,
        feature: f as unknown as PondRow['feature'],
      });
    }
    // Sort key uses the scaled fill time so the leaderboard ordering
    // tracks the what-if slider in real time — the worst pond at design
    // may not be the worst at 2× design, and the slider should reveal
    // that.
    result.sort((a, b) => a.fillTimeS - b.fillTimeS);
    return { ponds: result, drainedCount: drained };
  }, [features, gullies, stormScale]);

  // Default-expanded if we have ≤ 8 non-drained ponds.
  const expanded = storedExpanded ?? ponds.length <= 8;

  if (ponds.length === 0 && drainedCount === 0) {
    // Friendly empty state: distinguishes "this run has no fill-times"
    // from a render bug. Most often hit when every pond is terminal
    // and has zero upstream catchment, so fillTimeS is undefined.
    return (
      <div className='text-[10px] italic text-slate-500 dark:text-stone-400'>
        {t('rainfallSummary.leaderboard.empty')}
      </div>
    );
  }

  const onRowClick = (row: PondRow) => {
    const props = (row.feature.properties ?? {}) as Record<string, unknown>;
    setSelectedFeature({ kind: 'pond', properties: props });
    setSelectedPondId(row.pondId);
    // Also surface the pond into the FeatureProperties drawer so the
    // user immediately sees the capacity chip + mid-storm warning
    // without a second click on the polygon.
    const f = row.feature;
    if (f && f.id && f.layerId) {
      // Cast back to the drawer's expected feature shape — we lose
      // the type-narrowing inside this row but the runtime shape is
      // the original GraphQL Feature.
      setDrawerFeature({
        ...f,
        onSave: createUpdateHandler(f.id, f.layerId),
      } as unknown as Parameters<typeof setDrawerFeature>[0]);
    }
  };

  // CSV export — operates on every pond in the layer (drained + active)
  // regardless of leaderboard expanded state. Engineers want the full
  // dataset so they can filter in Excel; the on-screen leaderboard is
  // for triage, the CSV is for the report.
  const onExportCsv = () => {
    const csv = buildRainfallCsv(features, gullies, stormScale, stormDurationH);
    const filename = safeRainfallCsvFilename(layerName);
    downloadFile(csv, filename, 'text/csv;charset=utf-8');
  };

  return (
    <div>
      <div className='flex items-center justify-between gap-2'>
        <button
          type='button'
          onClick={() => setExpanded(!expanded)}
          className='flex flex-1 items-center gap-1.5 text-[11px] font-medium text-slate-700 hover:text-slate-900 dark:text-stone-300 dark:hover:text-stone-100'>
          {expanded ? <ChevronUp className='size-3' /> : <ChevronDown className='size-3' />}
          {t('rainfallSummary.leaderboard.title')}
          <span className='text-slate-500 dark:text-stone-400'>({ponds.length})</span>
        </button>
        {/* Export CSV — small + right-aligned next to the chevron so
            it doesn't compete with the leaderboard heading. Available
            even when collapsed: operates on the underlying layer data,
            not the rendered table. Shared links don't download data. */}
        {!getLinkSessionId() && (
          <button
            type='button'
            onClick={onExportCsv}
            title={t('rainfallSummary.exportCsvTooltip') as string}
            className='inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-stone-300 dark:hover:bg-stone-700/40 dark:hover:text-stone-100'>
            <Download className='size-3' />
            {t('rainfallSummary.exportCsv')}
          </button>
        )}
      </div>
      {expanded && (
        <div className='mt-1 max-h-[240px] overflow-y-auto'>
          <table className='w-full text-[11px]'>
            <thead className='sticky top-0 bg-white/95 text-left text-[10px] uppercase tracking-wide text-slate-500 dark:bg-stone-900/95 dark:text-stone-400'>
              <tr>
                <th className='py-0.5 pr-1'>#</th>
                <th className='py-0.5 pr-1'>{t('rainfallSummary.leaderboard.pond')}</th>
                <th className='py-0.5 pr-1 text-right'>
                  {t('rainfallSummary.leaderboard.fillTime')}
                </th>
                <th
                  className='py-0.5 pr-1 text-left'
                  title={t('rainfallSummary.leaderboard.holdsTooltip') as string}>
                  {t('rainfallSummary.leaderboard.holds')}
                </th>
                <th
                  className='py-0.5 pr-1 text-center'
                  title={t('rainfallSummary.leaderboard.statusTooltip') as string}>
                  {t('rainfallSummary.leaderboard.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {ponds.map((row, i) => {
                // Capture ratio uses the per-pond scaled total runoff
                // (matches the inspector's CapacityBlock). Fall back to
                // the layer-level scaled runoff when the per-pond value
                // is missing — keeps small/edge ponds from rendering
                // a blank ratio cell.
                const denomM3 =
                  row.scaledTotalRunoffM3 ??
                  (totalRunoffM3 != null
                    ? scaledTotalRunoffM3(totalRunoffM3, stormScale)
                    : null);
                const captureRatio =
                  denomM3 != null && denomM3 > 0 ? row.volumeM3 / denomM3 : undefined;
                const pct =
                  captureRatio != null
                    ? Math.min(100, Math.max(0, Math.round(captureRatio * 100)))
                    : null;
                const overflowsMidStorm =
                  stormDurationS != null && row.fillTimeS < stormDurationS;
                let dotClass = 'bg-emerald-500';
                if (overflowsMidStorm) dotClass = 'bg-rose-500';
                else if (captureRatio != null && captureRatio < 1) dotClass = 'bg-amber-500';
                return (
                  <tr
                    key={row.pondId}
                    onClick={() => onRowClick(row)}
                    title={t('rainfallSummary.leaderboard.rowTooltip') as string}
                    className='cursor-pointer border-t border-slate-100 hover:bg-slate-50 dark:border-stone-800 dark:hover:bg-stone-800/40'>
                    <td
                      className={`py-0.5 pr-1 font-mono tabular-nums ${
                        i === 0
                          ? 'font-semibold text-rose-600 dark:text-rose-400'
                          : i === 1
                            ? 'font-medium text-amber-600 dark:text-amber-400'
                            : i === 2
                              ? 'font-medium text-emerald-600 dark:text-emerald-400'
                              : 'text-slate-500 dark:text-stone-400'
                      }`}>
                      #{i + 1}
                    </td>
                    <td
                      className='max-w-[80px] truncate py-0.5 pr-1 font-mono'
                      title={row.pondId}>
                      {row.pondId}
                    </td>
                    <td
                      className={`py-0.5 pr-1 text-right font-mono tabular-nums ${
                        isScaled ? 'italic text-sky-700 dark:text-sky-300' : ''
                      }`}>
                      {isScaled ? `~${fmtDurationS(row.fillTimeS)}` : fmtDurationS(row.fillTimeS)}
                    </td>
                    <td className='py-0.5 pr-1 text-left'>
                      {pct != null ? (
                        // Left-align the bar; right-align the % readout
                        // inside a fixed-width slot so 5%, 50%, 100% all
                        // line up under the same right edge. Without the
                        // fixed slot, tabular-nums alone won't fix the
                        // ragged-edge perception when the leading digit
                        // count varies.
                        <div className='flex items-center gap-1'>
                          <div className='h-1 w-8 overflow-hidden rounded-full bg-slate-200 dark:bg-stone-700'>
                            <div
                              className='h-full bg-sky-500'
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className='inline-block w-9 text-right font-mono tabular-nums'>
                            {pct}%
                          </span>
                        </div>
                      ) : (
                        <span className='text-slate-400'>—</span>
                      )}
                    </td>
                    <td className='py-0.5 pr-1 text-center'>
                      <span className={`inline-block size-2 rounded-full ${dotClass}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {drainedCount > 0 && (
        <div className='mt-1 text-[10px] italic text-slate-500 dark:text-stone-400'>
          {t('rainfallSummary.leaderboard.drainedFooter', { count: drainedCount })}
        </div>
      )}
    </div>
  );
}
